import React, { useState, useEffect, useRef, useCallback } from "react";
import { Breadcrumb, NavBar, SideNav } from "../../components";
import { Button, Modal, Label } from "flowbite-react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { FiTrash2, FiEye } from "react-icons/fi";
import { AiOutlineLoading } from "react-icons/ai";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { timestampToTimeAgo } from "../../config/firebase";
import {
  batchPublishProgrammes,
  batchDeleteProgrammes,
  batchRefineProgrammes,
  createProgramme,
  deleteProgramme,
  fetchProgrammes,
  publishProgramme,
  updateProgramme,
} from "../../redux/slices/programmes";
import { capitalize } from "../../helpers/handlers/methods";
import PostEditor from "../CreatePosts/PostEditor";
import AiRestructureModal from "../CreatePosts/AiRestructureModal";
import StructuredFieldsPanel from "../CreatePosts/StructuredFieldsPanel";
import { restructureContent } from "../../config/openai";
import api from "../../config/axios";
import { wrapPostPreviewHtml } from "../../helpers/postBodyHtml";
import { sanitizeHtmlForQuill } from "../../helpers/sanitizeQuillHtml";
import { toast } from "react-toastify";
import {
  emptyProgrammeFields,
  mergeExtractedIntoProgramme,
  programmeFieldsFromRecord,
  programmePayloadFromFields,
} from "../../helpers/advisorVocab";

const TAB_PUBLISHED = "published";
const TAB_DRAFT = "draft";

const PENDING = {
  BATCH_PUBLISH: "batchPublish",
  BATCH_REFINE: "batchRefine",
  BATCH_DELETE: "batchDelete",
  PUBLISH_ROW: "publishRow",
  DELETE_ONE: "deleteOne",
  SAVE_DRAFT: "saveDraft",
  CREATE_PUBLISHED: "createPublished",
  UPDATE_PROGRAMME: "updateProgramme",
};

const ACTION_SPINNER = (
  <AiOutlineLoading className="h-6 w-6 animate-spin" />
);

function formatRestructureError(err) {
  if (err == null) {
    return "Could not restructure content.";
  }
  if (typeof err === "string") {
    return err;
  }
  if (typeof err === "object") {
    const msg = err.message || err.error?.message;
    if (typeof msg === "string" && msg.trim()) {
      return msg;
    }
  }
  return "Could not restructure content.";
}

const ProgrammeView = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const programmeStore = useSelector((state) => state.programmes);
  const { pageLoading } = programmeStore;
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingRowId, setPendingRowId] = useState(null);
  const [openProgrammeModal, setOpenProgrammeModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedProgrammeId, setSelectedPostId] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [bodyContent, setBodyContent] = useState();
  const [titleContent, setTitleContent] = useState();
  const [selectedForBatch, setSelectedForBatch] = useState([]);
  const [batchResults, setBatchResults] = useState(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiRestructureLoading, setAiRestructureLoading] = useState(false);
  const [editorSyncCount, setEditorSyncCount] = useState(0);
  const [structuredFields, setStructuredFields] = useState(emptyProgrammeFields);
  const [extractLoading, setExtractLoading] = useState(false);
  const [completenessScore, setCompletenessScore] = useState(null);
  const selectAllCheckboxRef = useRef(null);
  const { id: postId } = useParams();

  const params = new URLSearchParams(location.search);
  const schoolName = params.get("school");
  const schoolIsDraft = params.get("isDraft") === "true";
  const deepLinkProgrammeId = params.get("programme");
  const openedDeepLinkRef = useRef(null);

  const [programmeTab, setProgrammeTab] = useState(
    schoolIsDraft ? TAB_DRAFT : TAB_PUBLISHED
  );

  const programmeListIsDraft = schoolIsDraft || programmeTab === TAB_DRAFT;

  const actionBusy =
    pendingAction != null || aiRestructureLoading || extractLoading;

  const clearPendingAction = () => {
    setPendingAction(null);
    setPendingRowId(null);
  };

  useEffect(() => {
    setProgrammeTab(schoolIsDraft ? TAB_DRAFT : TAB_PUBLISHED);
  }, [postId, schoolIsDraft]);

  useEffect(() => {
    dispatch(
      fetchProgrammes({
        postId,
        isDraft: programmeListIsDraft,
      })
    );
  }, [dispatch, postId, programmeListIsDraft]);

  useEffect(() => {
    setSelectedForBatch([]);
  }, [postId, programmeListIsDraft]);

  const visibleIds = programmeStore.data.map((r) => r.id);

  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (!el) return;
    const n = visibleIds.length;
    const sel = visibleIds.filter((id) => selectedForBatch.includes(id)).length;
    el.indeterminate = n > 0 && sel > 0 && sel < n;
  }, [visibleIds, selectedForBatch]);

  const loadMoreContent = async (e) => {
    e.preventDefault();
    const lastDocId = programmeStore.lastDocId;
    dispatch(
      fetchProgrammes({
        postId,
        isDraft: programmeListIsDraft,
        lastDocId,
      })
    );
  };

  const refreshList = () => {
    dispatch(
      fetchProgrammes({
        postId,
        isDraft: programmeListIsDraft,
      })
    );
  };

  const handleViewProgram = useCallback((row) => {
    if (!row) return;
    setSelectedData(row);
    setTitleContent(row.title);
    setBodyContent(row.body ?? "");
    setStructuredFields(programmeFieldsFromRecord(row));
    setCompletenessScore(
      typeof row.completenessScore === "number" ? row.completenessScore : null
    );
    setEditorSyncCount((c) => c + 1);
    setOpenProgrammeModal(true);
  }, []);

  useEffect(() => {
    openedDeepLinkRef.current = null;
  }, [deepLinkProgrammeId, postId]);

  useEffect(() => {
    if (!deepLinkProgrammeId || pageLoading) return;
    if (openedDeepLinkRef.current === deepLinkProgrammeId) return;

    const fromList = programmeStore.data.find(
      (row) => row.id === deepLinkProgrammeId
    );
    if (fromList) {
      openedDeepLinkRef.current = deepLinkProgrammeId;
      handleViewProgram(fromList);
      return;
    }

    let cancelled = false;
    const loadById = async () => {
      const tryFetch = async (isDraft) => {
        try {
          const response = await api.get(
            `/admin/programmes/${deepLinkProgrammeId}?isDraft=${isDraft}`
          );
          return response.data.payload;
        } catch {
          return null;
        }
      };

      let rec = await tryFetch(programmeListIsDraft);
      let foundAsDraft = programmeListIsDraft;
      if (!rec) {
        rec = await tryFetch(!programmeListIsDraft);
        foundAsDraft = !programmeListIsDraft;
      }
      if (cancelled || !rec) return;

      openedDeepLinkRef.current = deepLinkProgrammeId;
      if (foundAsDraft !== programmeListIsDraft) {
        setProgrammeTab(foundAsDraft ? TAB_DRAFT : TAB_PUBLISHED);
      }
      handleViewProgram({ ...rec, id: rec.id || deepLinkProgrammeId });
    };

    loadById();
    return () => {
      cancelled = true;
    };
  }, [
    deepLinkProgrammeId,
    pageLoading,
    programmeStore.data,
    programmeListIsDraft,
    handleViewProgram,
  ]);

  // TODO(ai-advisor): Same as CreatePosts — prompt extract before save when
  // programme structured fields are empty. Body HTML is what apps render;
  // typed fields are what Match uses. Bulk backfill: Completeness page.
  const handleAutoExtract = async () => {
    if (!bodyContent || !String(bodyContent).trim()) {
      toast.error("Add body content before extracting.");
      return;
    }
    try {
      setExtractLoading(true);
      const response = await api.post("/admin/ai/extract-fields", {
        category: "programme",
        body: bodyContent,
      });
      const extracted = response.data.payload || {};
      setStructuredFields((prev) =>
        mergeExtractedIntoProgramme(prev, extracted)
      );
      if (typeof extracted.completenessScore === "number") {
        setCompletenessScore(extracted.completenessScore);
      }
      toast.success("Structured fields extracted — review before saving.");
    } catch (err) {
      const msg =
        err.response?.data?.errors?.global ||
        err.message ||
        "Extraction failed";
      toast.error(msg);
    } finally {
      setExtractLoading(false);
    }
  };

  const handleAiRestructureConfirm = async ({ extraUserInstructions }) => {
    try {
      setAiRestructureLoading(true);
      const innerHtml = await restructureContent(bodyContent, {
        extraUserInstructions: extraUserInstructions || undefined,
        contentKind: "programme",
      });
      const safe = sanitizeHtmlForQuill(innerHtml);
      const wrapped = wrapPostPreviewHtml(safe);
      setBodyContent(wrapped);
      setEditorSyncCount((c) => c + 1);
      setAiModalOpen(false);
      toast.success("Programme description refined.");
    } catch (err) {
      const message = formatRestructureError(err);
      toast.error(message);
      console.error(err);
    } finally {
      setAiRestructureLoading(false);
    }
  };

  const handleCreate = async (saveAsDraft) => {
    setPendingAction(
      saveAsDraft ? PENDING.SAVE_DRAFT : PENDING.CREATE_PUBLISHED
    );
    try {
      const data = {
        title: titleContent,
        body: bodyContent,
        articleId: postId,
        ...programmePayloadFromFields(structuredFields),
      };
      const resultAction = await dispatch(
        createProgramme({ schoolIsDraft, saveAsDraft, data })
      );
      if (createProgramme.fulfilled.match(resultAction)) {
        setOpenProgrammeModal(false);
        refreshList();
      }
    } catch (error) {
      console.error(error);
    } finally {
      clearPendingAction();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedData) {
      setPendingAction(PENDING.UPDATE_PROGRAMME);
      try {
        const data = {
          title: titleContent,
          body: bodyContent,
          articleId: selectedData.articleId || postId,
          ...programmePayloadFromFields(structuredFields),
        };
        const resultAction = await dispatch(
          updateProgramme({
            id: selectedData.id,
            data,
            isDraft: programmeListIsDraft,
          })
        );
        if (updateProgramme.fulfilled.match(resultAction)) {
          setOpenProgrammeModal(false);
          refreshList();
        }
      } catch (error) {
        console.error(error);
      } finally {
        clearPendingAction();
      }
      return;
    }
    if (schoolIsDraft) {
      await handleCreate(true);
    } else {
      await handleCreate(false);
    }
  };

  const handleSaveDraft = async (e) => {
    e.preventDefault();
    if (selectedData) return;
    await handleCreate(true);
  };

  const handlePublishProgrammeRow = async (id) => {
    setPendingAction(PENDING.PUBLISH_ROW);
    setPendingRowId(id);
    try {
      const resultAction = await dispatch(publishProgramme({ id }));
      if (publishProgramme.fulfilled.match(resultAction)) {
        dispatch(
          fetchProgrammes({
            postId,
            isDraft: true,
          })
        );
        setSelectedForBatch((prev) => prev.filter((x) => x !== id));
      }
    } catch (error) {
      console.error(error);
    } finally {
      clearPendingAction();
    }
  };

  const handleBatchPublish = async () => {
    if (selectedForBatch.length === 0) return;
    setPendingAction(PENDING.BATCH_PUBLISH);
    try {
      const resultAction = await dispatch(
        batchPublishProgrammes({ ids: selectedForBatch })
      );
      if (batchPublishProgrammes.fulfilled.match(resultAction)) {
        setBatchResults(resultAction.payload);
        setSelectedForBatch([]);
        dispatch(
          fetchProgrammes({
            postId,
            isDraft: true,
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      clearPendingAction();
    }
  };

  const handleBatchDelete = async () => {
    if (selectedForBatch.length === 0) return;
    setPendingAction(PENDING.BATCH_DELETE);
    try {
      const resultAction = await dispatch(
        batchDeleteProgrammes({
          ids: selectedForBatch,
          isDraft: programmeListIsDraft,
          articleId: postId,
        })
      );
      if (batchDeleteProgrammes.fulfilled.match(resultAction)) {
        setBatchResults(resultAction.payload);
        setSelectedForBatch([]);
        dispatch(
          fetchProgrammes({
            postId,
            isDraft: programmeListIsDraft,
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      clearPendingAction();
    }
  };

  const handleBatchRefine = async () => {
    if (selectedForBatch.length === 0) return;
    setPendingAction(PENDING.BATCH_REFINE);
    try {
      const resultAction = await dispatch(
        batchRefineProgrammes({
          ids: selectedForBatch,
          save: true,
        })
      );
      if (batchRefineProgrammes.fulfilled.match(resultAction)) {
        setBatchResults(resultAction.payload);
        setSelectedForBatch([]);
        dispatch(
          fetchProgrammes({
            postId,
            isDraft: programmeListIsDraft,
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      clearPendingAction();
    }
  };

  const toggleBatchRow = (id) => {
    setSelectedForBatch((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllVisible = () => {
    const allOn =
      visibleIds.length > 0 &&
      visibleIds.every((id) => selectedForBatch.includes(id));
    if (allOn) {
      setSelectedForBatch((prev) =>
        prev.filter((id) => !visibleIds.includes(id))
      );
    } else {
      setSelectedForBatch((prev) => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const handleDeleteProgramme = async (id) => {
    setPendingAction(PENDING.DELETE_ONE);
    try {
      const resultAction = await dispatch(
        deleteProgramme({ id, isDraft: programmeListIsDraft })
      );
      if (deleteProgramme.fulfilled.match(resultAction)) {
        setOpenDeleteModal(false);
      }
    } catch {
    } finally {
      clearPendingAction();
    }
  };

  const activeTabStyle =
    "inline-block p-4 text-blue-600 border-b-2 border-blue-600 rounded-t-lg active dark:text-blue-500 dark:border-blue-500";
  const inactiveTabStyle =
    "inline-block p-4 border-b-2 border-transparent rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300";

  const Programme = ({ row, white }) => {
    return (
      <tr
        className={`border-b ${
          white !== true ? "bg-white" : "bg-gray-50"
        } dark:bg-gray-800 dark:border-gray-700`}
      >
        <td className="px-4 py-4 w-12">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-700"
            checked={selectedForBatch.includes(row.id)}
            onChange={() => toggleBatchRow(row.id)}
            aria-label={`Select ${row.title}`}
          />
        </td>
        <td className="px-6 py-4 text-lg text-black dark:text-white">
          {row.title}
        </td>
        <td className="px-6 py-4">{timestampToTimeAgo(row.createdAt)}</td>
        <td className="px-6 py-4 flex flex-wrap gap-4">
          <Button
            color="purple"
            size="sm"
            className="relative flex items-center justify-center group"
            onClick={() => handleViewProgram(row)}
          >
            <FiEye className="h-5 w-5" />
            <span className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-sm bg-black text-white px-2 py-1 rounded whitespace-nowrap">
              View
            </span>
          </Button>

          {programmeListIsDraft && (
            <Button
              color="green"
              size="sm"
              className="relative flex items-center justify-center group"
              onClick={() => handlePublishProgrammeRow(row.id)}
              disabled={actionBusy}
              isProcessing={
                pendingAction === PENDING.PUBLISH_ROW &&
                pendingRowId === row.id
              }
              processingSpinner={ACTION_SPINNER}
            >
              Publish
              <span className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-sm bg-black text-white px-2 py-1 rounded whitespace-nowrap z-10">
                Publish to live programmes
              </span>
            </Button>
          )}

          <Button
            color="red"
            size="sm"
            className="relative flex items-center justify-center group"
            onClick={() => {
              setSelectedPostId(row.id);
              setOpenDeleteModal(true);
            }}
            disabled={actionBusy}
          >
            <FiTrash2 className="h-5 w-5" />
            <span className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-sm bg-black text-white px-2 py-1 rounded whitespace-nowrap">
              Delete
            </span>
          </Button>
        </td>
      </tr>
    );
  };

  const list = programmeStore.data.map((item, index) => (
    <Programme key={item.id} row={item} white={index % 2 === 0} />
  ));

  return (
    <>
      <div className="antialiased bg-gray-50 dark:bg-gray-900">
        <NavBar />
        <SideNav />
        <Modal
          show={batchResults != null}
          size="xl"
          onClose={() => setBatchResults(null)}
        >
          <Modal.Header>Batch action results</Modal.Header>
          <Modal.Body>
            {batchResults && (
              <div className="text-left space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Attempted: <strong>{batchResults.attempted}</strong>
                  {" · "}
                  Succeeded: <strong>{batchResults.succeeded}</strong>
                  {" · "}
                  Failed: <strong>{batchResults.failed}</strong>
                </p>
                <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
                  <table className="w-full text-sm text-left text-gray-600 dark:text-gray-300">
                    <thead className="text-xs uppercase bg-gray-50 dark:bg-gray-700 sticky top-0">
                      <tr>
                        <th className="px-3 py-2">Id</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(batchResults.results || []).map((r) => (
                        <tr
                          key={r.id}
                          className="border-t border-gray-100 dark:border-gray-600"
                        >
                          <td className="px-3 py-2 font-mono text-xs break-all">
                            {r.id}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {r.success ? (
                              <span className="text-green-600 dark:text-green-400">
                                Success
                              </span>
                            ) : (
                              <span className="text-red-600 dark:text-red-400">
                                Failed{r.status != null ? ` (${r.status})` : ""}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 break-words">
                            {r.success
                              ? r.programme?.title ??
                                (typeof r.refinedBody === "string" ? "Refined" : "—")
                              : r.error ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end">
                  <Button color="gray" onClick={() => setBatchResults(null)}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </Modal.Body>
        </Modal>

        <Modal
          show={openDeleteModal}
          size="md"
          onClose={() => setOpenDeleteModal(false)}
          popup
        >
          <Modal.Header />
          <Modal.Body>
            <div className="text-center">
              <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
              <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                Are you sure you want to delete this programme?
              </h3>
              <div className="flex justify-center gap-4">
                <Button
                  color="failure"
                  onClick={() => handleDeleteProgramme(selectedProgrammeId)}
                  disabled={actionBusy}
                  isProcessing={pendingAction === PENDING.DELETE_ONE}
                  processingSpinner={ACTION_SPINNER}
                >
                  {"Yes, I'm sure"}
                </Button>
                <Button
                  color="gray"
                  onClick={() => setOpenDeleteModal(false)}
                  disabled={actionBusy}
                >
                  No, cancel
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          show={openProgrammeModal}
          size="2xl"
          onClose={() => !actionBusy && setOpenProgrammeModal(false)}
          popup
        >
          <Modal.Header />
          <Modal.Body>
            <div className="text-center">
              <AiRestructureModal
                show={aiModalOpen}
                onClose={() => !aiRestructureLoading && setAiModalOpen(false)}
                onConfirm={handleAiRestructureConfirm}
                isLoading={aiRestructureLoading}
              />
              <form
                className="flex flex-col w-full gap-6"
                onSubmit={handleSubmit}
              >
                <div className="">
                  <input
                    type="text"
                    value={titleContent}
                    className="font-display block py-2.5 px-0 w-full text-4xl text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-purple-500 focus:outline-none focus:ring-0 focus:border-purple-600 peer"
                    placeholder="Enter Title"
                    onChange={(e) => setTitleContent(e.target.value)}
                    required
                  />
                </div>
                {selectedData?.sourceUrl &&
                  String(selectedData.sourceUrl).trim() !== "" && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-left text-sm dark:border-gray-600 dark:bg-gray-700/50">
                      <span className="font-medium text-gray-700 dark:text-gray-200">
                        Original source
                      </span>
                      <div className="mt-1 truncate">
                        <a
                          href={String(selectedData.sourceUrl).trim()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-600 hover:underline dark:text-purple-400"
                        >
                          {String(selectedData.sourceUrl).trim()}
                        </a>
                      </div>
                    </div>
                  )}
                <PostEditor
                  key={selectedData ? selectedData.id : "new-programme"}
                  value={selectedData ? selectedData.body : undefined}
                  editorSyncCount={editorSyncCount}
                  syncFromStoredBody={bodyContent}
                  showAiFab
                  onAiFabClick={() => setAiModalOpen(true)}
                  aiFabDisabled={actionBusy}
                  updateBodyContent={(content) => {
                    setBodyContent(content);
                  }}
                />
                <StructuredFieldsPanel
                  category="programme"
                  value={structuredFields}
                  onChange={setStructuredFields}
                  onExtract={handleAutoExtract}
                  extractLoading={extractLoading}
                  extractDisabled={actionBusy || !bodyContent}
                  completenessScore={completenessScore}
                />
                {selectedData && (
                  <Button
                    color="purple"
                    size="sm"
                    type="submit"
                    disabled={actionBusy}
                    isProcessing={pendingAction === PENDING.UPDATE_PROGRAMME}
                    processingSpinner={ACTION_SPINNER}
                  >
                    Update programme
                  </Button>
                )}
                {!selectedData && (
                  <div className="flex flex-wrap justify-center gap-3">
                    <Button
                      color="purple"
                      size="sm"
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={actionBusy}
                      isProcessing={pendingAction === PENDING.SAVE_DRAFT}
                      processingSpinner={ACTION_SPINNER}
                    >
                      Save as draft
                    </Button>
                    {!schoolIsDraft && (
                      <Button
                        color="red"
                        size="sm"
                        type="submit"
                        disabled={actionBusy}
                        isProcessing={
                          pendingAction === PENDING.CREATE_PUBLISHED
                        }
                        processingSpinner={ACTION_SPINNER}
                      >
                        Create published
                      </Button>
                    )}
                  </div>
                )}
              </form>
            </div>
          </Modal.Body>
        </Modal>

        <main className="p-4 md:ml-64 h-auto pt-20">
          <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
            <Breadcrumb />
            <div className="text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700 mt-2">
              <ul className="flex flex-wrap -mb-px">
                <li className="me-2">
                  <button
                    type="button"
                    className={`${
                      !schoolIsDraft && programmeTab === TAB_PUBLISHED
                        ? activeTabStyle
                        : inactiveTabStyle
                    } ${
                      schoolIsDraft
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer"
                    }`}
                    disabled={schoolIsDraft}
                    title={
                      schoolIsDraft
                        ? "Draft schools only have draft programmes"
                        : undefined
                    }
                    onClick={() => !schoolIsDraft && setProgrammeTab(TAB_PUBLISHED)}
                  >
                    Published programmes
                  </button>
                </li>
                <li className="me-2">
                  <button
                    type="button"
                    className={`${
                      schoolIsDraft || programmeTab === TAB_DRAFT
                        ? activeTabStyle
                        : inactiveTabStyle
                    } cursor-pointer`}
                    onClick={() => setProgrammeTab(TAB_DRAFT)}
                  >
                    Draft programmes
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="flex mt-4">
            {schoolName && (
              <Label className="font-display text-3xl font-bold grow">
                {capitalize(schoolName)}
              </Label>
            )}
            <div className="flex flex-wrap items-center gap-2 justify-end">
              {programmeListIsDraft && (
                <>
                  <Button
                    color="green"
                    size="sm"
                    disabled={
                      selectedForBatch.length === 0 || actionBusy
                    }
                    isProcessing={pendingAction === PENDING.BATCH_PUBLISH}
                    processingSpinner={ACTION_SPINNER}
                    onClick={handleBatchPublish}
                  >
                    Publish selected ({selectedForBatch.length})
                  </Button>
                  <Button
                    color="purple"
                    size="sm"
                    disabled={selectedForBatch.length === 0 || actionBusy}
                    isProcessing={pendingAction === PENDING.BATCH_REFINE}
                    processingSpinner={ACTION_SPINNER}
                    onClick={handleBatchRefine}
                  >
                    Refine selected ({selectedForBatch.length})
                  </Button>
                </>
              )}
              <Button
                color="failure"
                size="sm"
                disabled={selectedForBatch.length === 0 || actionBusy}
                isProcessing={pendingAction === PENDING.BATCH_DELETE}
                processingSpinner={ACTION_SPINNER}
                onClick={handleBatchDelete}
              >
                Delete selected ({selectedForBatch.length})
              </Button>
              <Button
                color="purple"
                className=""
                onClick={() => {
                  setSelectedData(null);
                  setTitleContent("");
                  setBodyContent("");
                  setStructuredFields(emptyProgrammeFields());
                  setCompletenessScore(null);
                  setEditorSyncCount((c) => c + 1);
                  setOpenProgrammeModal(true);
                }}
              >
                Add Programme
              </Button>
            </div>
          </div>
          {programmeStore.totalCount != null && programmeStore.data.length > 0 && (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Showing {programmeStore.data.length} / {programmeStore.totalCount}{" "}
              {programmeListIsDraft ? "draft " : ""}
              programmes
            </p>
          )}
          {visibleIds.length > 0 && selectedForBatch.length > 0 && (
            <button
              type="button"
              className="mt-2 text-sm text-purple-600 hover:underline dark:text-purple-400"
              onClick={() => setSelectedForBatch([])}
            >
              Clear selection
            </button>
          )}
          {schoolIsDraft && (
            <p className="mt-2 text-sm italic text-gray-500 dark:text-gray-400 cursor-default select-none">
              This school is still a draft. Programmes are listed from draft
              only.
            </p>
          )}
          {pageLoading && list.length === 0 ? (
            <div className="animate-puls mt-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-4 p-4">
                <div className="grid grid-cols-4">
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                </div>
                <hr className="mt-2" />
                <div className="grid grid-cols-4 mt-4 items-center">
                  <div className="flex">
                    <div>
                      <div className="mt-2 h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-64" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-32" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                </div>
                <div className="grid grid-cols-4 mt-4 items-center">
                  <div className="flex">
                    <div>
                      <div className="mt-2 h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-64" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-32" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="relative overflow-x-auto shadow-md sm:rounded-lg mt-4">
                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                  <thead className="text-xs text-gray-600 bg-white uppercase  dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="px-4 py-3 w-12">
                        <input
                          ref={selectAllCheckboxRef}
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-700"
                          checked={
                            visibleIds.length > 0 &&
                            visibleIds.every((id) =>
                              selectedForBatch.includes(id)
                            )
                          }
                          onChange={toggleSelectAllVisible}
                          aria-label="Select all programmes on this page"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Title
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Created At
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>{list}</tbody>
                </table>
              </div>
              <div className="flex justify-center mt-4">
                {programmeStore.hasMoreContent && (
                  <Button
                    size="sm"
                    color="gray"
                    onClick={loadMoreContent}
                    disabled={pageLoading}
                    isProcessing={pageLoading}
                    processingSpinner={
                      <AiOutlineLoading className="h-6 w-6 animate-spin" />
                    }
                  >
                    Load More
                  </Button>
                )}
              </div>

              {list.length === 0 && (
                <center className="text-gray-400 italic">
                  No programmes available
                </center>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default ProgrammeView;
